import * as XLSX from 'xlsx';
import { formatEurPerKwh } from './financialCalculations';
import type { FinancialKpis, YearCashflow, CapexBreakdown, SensitivityItem } from './financialCalculations';
import type { FinancialParams } from './financialDefaults';

export function exportToExcel(
  kpis: FinancialKpis,
  cashflows: YearCashflow[],
  capex: CapexBreakdown,
  sensitivity: SensitivityItem[],
  params: FinancialParams,
) {
  const wb = XLSX.utils.book_new();

  // ── KPI's ──
  const kpiData = [
    ['KPI', 'Waarde'],
    ['NPV (na belasting)', kpis.npvAfterTax],
    ['IRR (na belasting)', `${kpis.irrAfterTax.toFixed(1)}%`],
    ['Equity IRR', `${kpis.equityIrr.toFixed(1)}%`],
    ['Terugverdientijd', kpis.paybackYear !== null ? `${kpis.paybackYear.toFixed(1)} jaar` : 'N/A'],
    // Vier decimalen via de gedeelde formatter: bij nul geleverde energie staat
    // hier "n.v.t." in plaats van €0,0000 LCOE en €0,3000 marge — de waarden die
    // de Wognum-invoer met pv 0 / gMax 0 / bP 0 / buP 0 en 19.000 MWh vraag
    // (100 % ongedekt, NPV −€14.001.448) opleverde.
    ['LCOE', formatEurPerKwh(kpis.lcoe, 4)],
    ['Marge/kWh', formatEurPerKwh(kpis.marginPerKwh, 4)],
    ['ROI', `${kpis.roi.toFixed(0)}%`],
    ['CAPEX totaal', kpis.totalCapex],
    ['Min DSCR', kpis.minDscr.toFixed(2)],
    ['Gem DSCR', kpis.avgDscr.toFixed(2)],
    ['CO₂ vermeden (ton)', kpis.totalCo2Avoided.toFixed(0)],
    ['CO₂ certificaatwaarde', kpis.co2CertificateValue],
    ['VPB tarief', `${params.vpbRate}%`],
    ['EIA aftrek', `${params.eiaPercentage}%`],
    ['MIA aftrek', `${params.miaPercentage}%`],
  ];
  const wsKpi = XLSX.utils.aoa_to_sheet(kpiData);
  wsKpi['!cols'] = [{ wch: 25 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsKpi, "KPI's");

  // ── Cashflows ──
  const cfHeaders = ['Jaar', 'Laden', 'Export', 'SDE++', 'Totaal Omzet', 'Grid', 'Brandstof', 'O&M PV', 'O&M BESS', 'Verzekering', 'BESS Verv.', 'Totaal OPEX', 'EBITDA', 'Afschrijving', 'Belastbaar ink.', 'VPB', 'Fiscaal voordeel', 'Schuldaflossing', 'Netto CF', 'Cumulatief', 'DCF', 'DSCR', 'CO₂ (ton)'];
  const cfRows = cashflows.map(cf => [
    cf.year, cf.chargingRevenue, cf.exportRevenue, cf.sdeRevenue, cf.totalRevenue,
    cf.gridCost, cf.fuelCost, cf.omPv, cf.omBess, cf.insurance, cf.bessReplacement,
    cf.totalOpex, cf.ebitda, cf.depreciation, cf.taxableIncome, cf.vpb, cf.fiscalIncentive,
    cf.debtService, cf.netCashflow, cf.cumulativeCashflow, cf.discountedCashflow,
    cf.dscr === Infinity ? '' : cf.dscr, cf.co2Avoided,
  ]);
  const wsCf = XLSX.utils.aoa_to_sheet([cfHeaders, ...cfRows]);
  wsCf['!cols'] = cfHeaders.map(() => ({ wch: 14 }));
  XLSX.utils.book_append_sheet(wb, wsCf, 'Cashflows');

  // ── CAPEX ──
  const capexData = [
    ['Component', 'Bedrag (€)'],
    ['PV', capex.pv],
    ['BESS', capex.bess],
    ['Backup', capex.backup],
    ['Laadinfra', capex.chargers],
    ['Netaansluiting', capex.gridConnection],
    ...(capex.customCapex > 0 ? [['Overige kosten', capex.customCapex]] : []),
    ['Subtotaal', capex.subtotal],
    ['Engineering', capex.engineering],
    ['Totaal', capex.total],
  ];
  const wsCx = XLSX.utils.aoa_to_sheet(capexData);
  wsCx['!cols'] = [{ wch: 20 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsCx, 'CAPEX');

  // ── Sensitiviteit ──
  const sensHeaders = ['Parameter', 'Base NPV', '-20% NPV', '+20% NPV', 'Impact -20%', 'Impact +20%'];
  const sensRows = sensitivity.map(s => [s.label, s.baseNpv, s.lowNpv, s.highNpv, s.impactLow, s.impactHigh]);
  const wsSens = XLSX.utils.aoa_to_sheet([sensHeaders, ...sensRows]);
  wsSens['!cols'] = sensHeaders.map(() => ({ wch: 16 }));
  XLSX.utils.book_append_sheet(wb, wsSens, 'Sensitiviteit');

  // Download
  XLSX.writeFile(wb, 'business_case_export.xlsx');
}
